import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import {
  Grid,
  Box,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import translate from "i18n/translate";
import useStyles from "./invoicesStyles";

import {
  SrAlert,
  SrButton,
  SrLabel,
  SrInput,
  Typography,
} from "../../../common";
import { PERSON_TYPE } from "./constants";

const isDisabledFiscalRegime = (rfc) => rfc.length < 12;

const getPersonType = (rfc) => {
  switch (rfc.length) {
    case 12:
      return PERSON_TYPE.MORAL;
    case 13:
      return PERSON_TYPE.PHYSICAL;

    default:
      return;
  }
};

const filterFiscalRegimeOptionsByRFC = (fiscalRegimeOptions, rfc) => {
  const personType = getPersonType(rfc);

  const defaultFiscalRegime = fiscalRegimeOptions[0];

  if (!personType) return [defaultFiscalRegime];

  const filteredFiscalRegimeOptions = fiscalRegimeOptions.filter(
    (fiscalRegime) =>
      [PERSON_TYPE.ANY, personType].indexOf(fiscalRegime.person_type) !== -1
  );

  return [defaultFiscalRegime, ...filteredFiscalRegimeOptions];
};

const InvoiceForm = ({
  initialValues,
  isAutomaticBilling,
  fiscalRegimeOptions,
  isLoadingTowns,
  isSubmitting,
  onReturn,
  onSubmit,
  onZipCodeChange,
  towns,
  validationSchema,
  error,
}) => {
  const classes = useStyles();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: true,
    onSubmit,
  });

  const [fiscalRegimeOptionsFiltered, setFiscalRegimeOptionsFiltered] =
    useState([fiscalRegimeOptions[0]]);

  const {
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    setFieldValue,
    validateField,
    handleSubmit,
  } = formik;

  useEffect(() => {
    if (!isDisabledFiscalRegime(values.rfc)) {
      setFiscalRegimeOptionsFiltered(
        filterFiscalRegimeOptionsByRFC(fiscalRegimeOptions, values.rfc)
      );
    }
  }, [
    values.rfc,
    setFiscalRegimeOptionsFiltered,
    fiscalRegimeOptions,
    setFieldValue,
  ]);

  return (
    <>
      <SrButton
        startIcon={<ArrowBackIcon color="primary" />}
        onClick={() => Promise.resolve().then(onReturn)}
      >
        <Typography variant="body1SemiBold">
          {translate("common.return")}
        </Typography>
      </SrButton>
      <Box
        maxWidth={720}
        display="flex"
        flexDirection="column"
        margin="auto"
        marginTop={6}
      >
        <form onSubmit={handleSubmit} className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2" className={classes.formHeading}>
                {translate(
                  isAutomaticBilling
                    ? "invoices.automaticBilling"
                    : "invoices.newBilling"
                )}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.entry.name">
                {([placeholder]) => (
                  <SrInput
                    label={translate("common.name")}
                    placeholder={placeholder}
                    name="name"
                    type="text"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.name && touched.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                  />
                )}
              </FormattedMessage>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.entry.rfc">
                {([placeholder]) => (
                  <SrInput
                    label={translate("common.rfc")}
                    placeholder={placeholder}
                    name="rfc"
                    type="text"
                    value={values.rfc}
                    onChange={(e) => {
                      setFieldValue(
                        "fiscal_regime",
                        fiscalRegimeOptionsFiltered[0]
                      );
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    error={Boolean(errors.rfc && touched.rfc)}
                    helperText={touched.rfc && errors.rfc}
                    fullWidth
                    inputProps={{ maxLength: 13 }}
                  />
                )}
              </FormattedMessage>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.fiscalRegime">
                {([label]) => (
                  <>
                    <SrLabel name="fiscal_regime">{label}</SrLabel>
                    <Box mt={1}>
                      {fiscalRegimeOptionsFiltered && (
                        <Autocomplete
                          className={
                            values.fiscal_regime.id === -1
                              ? classes.placeholder
                              : ""
                          }
                          id="fiscal_regime"
                          options={fiscalRegimeOptionsFiltered}
                          value={values.fiscal_regime}
                          getOptionLabel={(option) => option.name}
                          getOptionDisabled={(option) => option.disabled}
                          onChange={(e, value) => {
                            setFieldValue("fiscal_regime", value);
                          }}
                          onBlur={handleBlur}
                          disableClearable
                          openOnFocus
                          disabled={isDisabledFiscalRegime(values.rfc)}
                          renderInput={(params) => (
                            <SrInput
                              {...params}
                              id="fiscal_regime"
                              name="fiscal_regime"
                              type="text"
                              error={Boolean(
                                errors.fiscal_regime && touched.fiscal_regime
                              )}
                              helperText={
                                touched.fiscal_regime && errors.fiscal_regime
                              }
                              fullWidth
                              inputprops={{
                                id: "fiscal_regime",
                              }}
                            />
                          )}
                        />
                      )}
                    </Box>
                  </>
                )}
              </FormattedMessage>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.entry.address">
                {([placeholder]) => (
                  <SrInput
                    label={translate("common.address")}
                    placeholder={placeholder}
                    name="address"
                    type="text"
                    value={values.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.address && touched.address)}
                    helperText={touched.address && errors.address}
                    fullWidth
                  />
                )}
              </FormattedMessage>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.entry.exterior">
                {([placeholder]) => (
                  <SrInput
                    label={translate("common.exterior")}
                    placeholder={placeholder}
                    name="number"
                    type="text"
                    value={values.number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.number && touched.number)}
                    helperText={touched.number && errors.number}
                    fullWidth
                  />
                )}
              </FormattedMessage>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.entry.interior">
                {([placeholder]) => (
                  <SrInput
                    label={translate("common.interior")}
                    placeholder={placeholder}
                    name="number_int"
                    type="text"
                    value={values.number_int}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.number_int && touched.number_int)}
                    helperText={touched.number_int && errors.number_int}
                    fullWidth
                  />
                )}
              </FormattedMessage>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.entry.pinCode">
                {([placeholder]) => (
                  <SrInput
                    label={translate("common.pinCode")}
                    placeholder={placeholder}
                    name="zip_code"
                    type="text"
                    value={values.zip_code}
                    onChange={async (event) => {
                      setFieldValue("town", "none");
                      handleChange(event);
                      await onZipCodeChange(event?.target?.value);
                      validateField("zip_code");
                    }}
                    InputProps={{
                      startAdornment: isLoadingTowns ? (
                        <InputAdornment position="start">
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : undefined,
                    }}
                    onBlur={handleBlur}
                    error={Boolean(
                      !isLoadingTowns && touched.zip_code && errors.zip_code
                    )}
                    helperText={
                      !isLoadingTowns && touched.zip_code && errors.zip_code
                    }
                    fullWidth
                  />
                )}
              </FormattedMessage>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage id="common.entry.colony">
                {(placeholder) => (
                  <SrInput
                    label={translate("common.colony")}
                    fullWidth
                    select
                    variant="outlined"
                    name="town"
                    id="town"
                    onBlur={handleBlur}
                    error={Boolean(touched.town && errors.town)}
                    helperText={touched.town && errors.town}
                    onChange={handleChange}
                    value={values.town}
                    disabled={!towns.length}
                  >
                    <MenuItem value="none" disabled>
                      {placeholder}
                    </MenuItem>
                    {towns?.map((item) => (
                      <MenuItem key={item.town} value={item.town}>
                        {item.town}
                      </MenuItem>
                    ))}
                  </SrInput>
                )}
              </FormattedMessage>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <SrAlert severity="error">{error}</SrAlert>
              </Grid>
            )}
          </Grid>
          <Box display="flex" justifyContent="flex-start" mt={4}>
            <SrButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {translate(
                isAutomaticBilling ? "common.register" : "common.generate"
              )}
            </SrButton>
          </Box>
        </form>
      </Box>
    </>
  );
};

InvoiceForm.propTypes = {
  initialValues: PropTypes.shape({}).isRequired,
  isAutomaticBilling: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onReturn: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  validationSchema: PropTypes.shape({}).isRequired,
  onZipCodeChange: PropTypes.func.isRequired,
  towns: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default InvoiceForm;
